'use client';

import { TZDate } from '@date-fns/tz';
import { format } from 'date-fns';

export const TIMEZONE_OPTIONS = [
  { label: 'Samoa Standard Time (UTC-11:00)', value: 'Pacific/Pago_Pago' },
  { label: 'Hawaii-Aleutian Standard Time (UTC-10:00)', value: 'Pacific/Honolulu' },
  { label: 'Alaska Standard Time (UTC-09:00)', value: 'America/Anchorage' },
  { label: 'Pacific Standard Time (UTC-08:00)', value: 'America/Los_Angeles' },
  { label: 'Mountain Standard Time (UTC-07:00)', value: 'America/Denver' },
  { label: 'Central Standard Time (UTC-06:00)', value: 'America/Chicago' },
  { label: 'Eastern Standard Time (UTC-05:00)', value: 'America/New_York' },
  { label: 'Atlantic Standard Time (UTC-04:00)', value: 'America/Halifax' },
  { label: 'Newfoundland Standard Time (UTC-03:30)', value: 'America/St_Johns' },
  { label: 'Brasilia Standard Time (UTC-03:00)', value: 'America/Sao_Paulo' },
  { label: 'South Georgia Time (UTC-02:00)', value: 'Atlantic/South_Georgia' },
  { label: 'Azores Standard Time (UTC-01:00)', value: 'Atlantic/Azores' },
  { label: 'Greenwich Mean Time (UTC+00:00)', value: 'Etc/UTC' },
  { label: 'Central European Time (UTC+01:00)', value: 'Europe/Berlin' },
  { label: 'Eastern European Time (UTC+02:00)', value: 'Europe/Kiev' },
  { label: 'Moscow Standard Time (UTC+03:00)', value: 'Europe/Moscow' },
  { label: 'Gulf Standard Time (UTC+04:00)', value: 'Asia/Dubai' },
  { label: 'Pakistan Standard Time (UTC+05:00)', value: 'Asia/Karachi' },
  { label: 'Indian Standard Time (UTC+05:30)', value: 'Asia/Kolkata' },
  { label: 'Nepal Time (UTC+05:45)', value: 'Asia/Kathmandu' },
  { label: 'Bangladesh Standard Time (UTC+06:00)', value: 'Asia/Dhaka' },
  { label: 'Myanmar Time (UTC+06:30)', value: 'Asia/Yangon' },
  { label: 'Indochina Time (UTC+07:00)', value: 'Asia/Bangkok' },
  { label: 'China Standard Time (UTC+08:00)', value: 'Asia/Shanghai' },
  { label: 'Japan Standard Time (UTC+09:00)', value: 'Asia/Tokyo' },
  {
    label: 'Australian Central Standard Time (UTC+09:30)',
    value: 'Australia/Adelaide',
  },
  { label: 'Australian Eastern Standard Time (UTC+10:00)', value: 'Australia/Sydney' },
  { label: 'Solomon Islands Time (UTC+11:00)', value: 'Pacific/Guadalcanal' },
  { label: 'New Zealand Standard Time (UTC+12:00)', value: 'Pacific/Auckland' },
  { label: 'Chatham Standard Time (UTC+12:45)', value: 'Pacific/Chatham' },
  { label: 'Tonga Time (UTC+13:00)', value: 'Pacific/Tongatapu' },
  { label: 'Line Islands Time (UTC+14:00)', value: 'Pacific/Kiritimati' },
];

export const MONTH_OPTIONS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const YEAR_OPTIONS = ['2022', '2023', '2024', '2025'];

export const currentMonth = () => new Date().toLocaleDateString(undefined, { month: 'long' });
export const currentYear = () => new Date().getFullYear().toString();

export const monthAsNumber = (monthName: string) => {
  const monthIndex = MONTH_OPTIONS.findIndex(
    (month) => month.toLowerCase() === monthName.toLowerCase()
  );

  return (monthIndex + 1).toString().padStart(2, '0');
};

export const monthAsString = (monthNumber: string) => {
  return MONTH_OPTIONS[parseInt(monthNumber, 10) - 1];
};

export const localDateToUTC = (value: Date): Date => {
  return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
};

export const localDateFromUTC = (value: Date): Date => {
  return new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
};

export const getDateFromServer = (value: string): Date => {
  //We leave this statement in to support legacy date strings

  if (value.length <= 6) {
    const date = new Date();
    const [hours, minutes] = value.split(':').map((value) => parseInt(value, 10));
    date.setHours(hours ?? 0);
    date.setMinutes(minutes ?? 0);
    return date;
  }
  return new Date(value);
};

export const getTimeFromServer = (value: string, timezone?: string): Date => {
  let date = getDateFromServer(value);
  if (timezone) {
    date = adjustDateForTimezone(date, timezone);
  }
  return date;
};

export const getTimeStringFromServer = (value: string, timezone?: string): string => {
  const date = getTimeFromServer(value, timezone);

  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const adjustDateForTimezone = (date: Date, timezone: string): Date => {
  return new TZDate(date, timezone);
};

export const getTimePickerStringFromServer = (value: string, timezone?: string): string => {
  const date = getDateFromServer(value);
  if (timezone) {
    const tzDate = new TZDate(date) as TZDate;
    return tzDate.withTimeZone(timezone).toLocaleTimeString('en-gb', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return date.toLocaleTimeString('en-gb', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const getMonthYearString = (value: Date | string) => {
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    year: '2-digit',
  });
};

export const getChartTooltipDateString = (value: Date | string) => {
  const date = new Date(value);

  const day = format(date, 'do');
  const month = format(date, 'MMM');
  const year = format(date, 'yyyy');
  return `${day} ${month} ${year}`;
};

export function getTournamentDateString(
  startDate?: Date | string,
  endDate?: Date | string
): null | [string, string] {
  if (!startDate || !endDate) {
    return null;
  }

  const startTS = format(new Date(startDate), 'MMM d');
  const endTS = format(new Date(endDate), 'MMM d, yyyy');

  return [startTS, endTS];
}
